import React from 'react';
import {
  Box, Flex, Text, Badge, Heading, Container,
  Table, Thead, Tbody, Tr, Th, Td,
  Card, CardHeader, CardBody, HStack, VStack, Divider,
  Stat, StatLabel, StatNumber, StatGroup, StatHelpText, StatArrow,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Icon, SimpleGrid
} from "@chakra-ui/react";
import { FiAlertCircle, FiCheckCircle, FiTrendingUp, FiDollarSign, FiFileText, FiShield, FiPieChart, FiUsers } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import HeaderMenus from './HeaderMenus';

export default function RetrospectiveDemoDashboard() {
  const rootCauseData = [
    { name: 'Missing Documentation / Unbilled Time', value: 40 },
    { name: 'NCCI Edit / Modifier Errors', value: 35 },
    { name: 'Medical Necessity / ICD-10 Specificity', value: 25 },
  ];
  const COLORS = ['#3182ce', '#dd6b20', '#e53e3e'];

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          
          {/* Header */}
          <Box>
            <Heading size="xl" color="blue.800" mb={2}>Retrospective Variance Analysis</Heading>
          </Box>

          {/* Executive Summary */}
          <Card shadow="md" borderTop="4px solid" borderColor="blue.500">
            <CardBody>
              <StatGroup>
                <Stat>
                  <StatLabel fontSize="md" color="gray.500">Claims Audited</StatLabel>
                  <StatNumber fontSize="3xl" filter="blur(4px)">10,000</StatNumber>
                  <StatHelpText>Historical 12-month lookback</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="md" color="gray.500">Actionable Variances</StatLabel>
                  <StatNumber fontSize="3xl" filter="blur(4px)">3,420</StatNumber>
                  <StatHelpText>
                    <StatArrow type="increase" />
                    <Text as="span" filter="blur(3px)">34.2%</Text> of total claims
                  </StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="md" color="gray.500">Revenue Leakage Identified</StatLabel>
                  <StatNumber fontSize="3xl" color="green.500" filter="blur(4px)">$1.85M</StatNumber>
                  <StatHelpText>Net new codes & units</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="md" color="gray.500">Preventable Denials</StatLabel>
                  <StatNumber fontSize="3xl" color="orange.500" filter="blur(4px)">$920K</StatNumber>
                  <StatHelpText>Impact on Clean Claim Rate</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="md" color="gray.500">Audit Risk & Overcoding</StatLabel>
                  <StatNumber fontSize="3xl" color="red.500" filter="blur(4px)">$415K</StatNumber>
                  <StatHelpText>Exposure mitigated</StatHelpText>
                </Stat>
              </StatGroup>
            </CardBody>
          </Card>

          {/* Analytics Row */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Root Cause Breakdown */}
            <Card shadow="md">
              <CardHeader bg="gray.100" py={3}>
                <HStack>
                  <Icon as={FiPieChart} color="blue.600" />
                  <Heading size="md" color="gray.700">Root Cause Breakdown</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <Flex h="250px" align="center" justify="center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={rootCauseData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {rootCauseData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36}/>
                    </PieChart>
                  </ResponsiveContainer>
                </Flex>
              </CardBody>
            </Card>

            {/* Targeted Education Insights */}
            <Card shadow="md">
              <CardHeader bg="gray.100" py={3}>
                <HStack>
                  <Icon as={FiUsers} color="blue.600" />
                  <Heading size="md" color="gray.700">Targeted Education Insights</Heading>
                </HStack>
              </CardHeader>
              <CardBody>
                <VStack align="stretch" spacing={5}>
                  <Box>
                    <Text fontWeight="bold" color="gray.700" mb={1}>Top Provider Needing Review</Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm">Dr. Smith (Orthopedics)</Text>
                      <Badge colorScheme="red">High Priority</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>Trend: Consistently under-documents time-based codes in operative notes.</Text>
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontWeight="bold" color="gray.700" mb={1}>Top Coder Trend</Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm">Team Alpha</Text>
                      <Badge colorScheme="orange">Medium Priority</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>Trend: Missing Modifier 59 on secondary procedures leading to CO-97 denials.</Text>
                  </Box>
                  <Divider />
                  <Box>
                    <Text fontWeight="bold" color="gray.700" mb={1}>Documentation Gap</Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm">Spinal Fusions</Text>
                      <Badge colorScheme="yellow">Monitor</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>Trend: Generic ICD-10 codes used instead of specific myelopathy/radiculopathy codes.</Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Retrospective Denial Analysis */}
          <Card shadow="md">
            <CardHeader bg="gray.100" py={3}>
              <HStack>
                <Icon as={FiFileText} color="blue.600" />
                <Heading size="md" color="gray.700">Line-Level Denial Analysis</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>CARC Code</Th>
                    <Th>Impact</Th>
                    <Th>Root Cause</Th>
                    <Th>Revedy Automated Mitigation</Th>
                    <Th>Appeal Success Prob.</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td fontWeight="bold">CO-252<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">Missing Medical Records</Text></Td>
                    <Td><Badge colorScheme="red"><Text as="span" filter="blur(3px)">450</Text> Claims</Badge><br/><Text fontSize="sm" mt={1} filter="blur(3px)">$320K</Text></Td>
                    <Td fontSize="sm">Generic ICD-10 codes failing automated medical necessity checks.</Td>
                    <Td fontSize="sm">Automated extraction of highly specific ICD-10s (e.g., radiculopathy, myelopathy) from the operative note to establish medical necessity upfront, bypassing manual record requests.</Td>
                    <Td><Badge colorScheme="green">95%</Badge></Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">CO-97<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">Bundled / Inclusive</Text></Td>
                    <Td><Badge colorScheme="red"><Text as="span" filter="blur(3px)">310</Text> Claims</Badge><br/><Text fontSize="sm" mt={1} filter="blur(3px)">$210K</Text></Td>
                    <Td fontSize="sm">Missing or incorrect NCCI modifiers.</Td>
                    <Td fontSize="sm">Automated application of Modifiers 59, XE, XP, XU based on exact anatomical site extraction from the technical report.</Td>
                    <Td><Badge colorScheme="green">92%</Badge></Td>
                  </Tr>
                  <Tr>
                    <Td fontWeight="bold">CO-16<br/><Text fontSize="xs" color="gray.500" fontWeight="normal">Lacks Information</Text></Td>
                    <Td><Badge colorScheme="red"><Text as="span" filter="blur(3px)">280</Text> Claims</Badge><br/><Text fontSize="sm" mt={1} filter="blur(3px)">$150K</Text></Td>
                    <Td fontSize="sm">Missing laterality or time-based documentation.</Td>
                    <Td fontSize="sm">Automated calculation of exact time-in/time-out from anesthesia notes to append correct units and laterality modifiers.</Td>
                    <Td><Badge colorScheme="green">88%</Badge></Td>
                  </Tr>
                </Tbody>
              </Table>
            </CardBody>
          </Card>

          {/* Uncaptured Revenue Opportunities */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Card shadow="md" borderTop="4px solid" borderColor="green.400">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <HStack>
                    <Icon as={FiTrendingUp} color="green.500" boxSize={5} />
                    <Heading size="sm">Uncaptured Time-Based Services</Heading>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Professional time is documented in the clinical narrative, but time-based codes were either missed entirely or billed inconsistently.
                  </Text>
                  <Badge colorScheme="green">Est. Impact: <Text as="span" filter="blur(3px)">$850K</Text></Badge>
                </VStack>
              </CardBody>
            </Card>
            <Card shadow="md" borderTop="4px solid" borderColor="green.400">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <HStack>
                    <Icon as={FiDollarSign} color="green.500" boxSize={5} />
                    <Heading size="sm">Missing Add-on Services</Heading>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Primary service billed correctly, but documented secondary procedures or techniques were omitted from the claim.
                  </Text>
                  <Badge colorScheme="green">Est. Impact: <Text as="span" filter="blur(3px)">$620K</Text></Badge>
                </VStack>
              </CardBody>
            </Card>
            <Card shadow="md" borderTop="4px solid" borderColor="green.400">
              <CardBody>
                <VStack align="start" spacing={3}>
                  <HStack>
                    <Icon as={FiCheckCircle} color="green.500" boxSize={5} />
                    <Heading size="sm">Under-coded Units</Heading>
                  </HStack>
                  <Text fontSize="sm" color="gray.600">
                    Billed 1 unit, but the clinical documentation explicitly details multiple distinct sites or instances that qualify for additional units.
                  </Text>
                  <Badge colorScheme="green">Est. Impact: <Text as="span" filter="blur(3px)">$380K</Text></Badge>
                </VStack>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Deep-Dive Case Studies */}
          <Card shadow="md">
            <CardHeader bg="gray.100" py={3}>
              <HStack>
                <Icon as={FiShield} color="blue.600" />
                <Heading size="md" color="gray.700">Structured Coding Variance Case Studies</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <Accordion allowMultiple defaultIndex={[0]}>
                
                {/* Case Study 1 */}
                <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md" mb={4}>
                  <AccordionButton _expanded={{ bg: 'blue.50' }}>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Badge colorScheme="purple">Potential Mitigation Identified</Badge>
                        <Text fontWeight="bold">Case #<Text as="span" filter="blur(3px)">3777576</Text>: The Medical Necessity Fix (Preventing CO-252)</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} bg="white">
                    <Text fontSize="sm" mb={4}>
                      <strong>Story:</strong> The organization billed a generic disc displacement code. The payer denied it requesting records (CO-252). Revedy's automated system read the chart, found severe myelopathy and radiculopathy, and recommended specific ICD-10 codes to establish medical necessity upfront.
                    </Text>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box p={3} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.100">
                        <Text fontWeight="bold" color="red.700" mb={2}>Original Claim (Denied)</Text>
                        <Text fontSize="sm"><strong>ICD-10:</strong> M51.25 (Generic Disc Displacement)</Text>
                        <Text fontSize="sm" mt={2} color="red.600"><em>Result: Automated denial requesting operative report.</em></Text>
                      </Box>
                      <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.100">
                        <Text fontWeight="bold" color="green.700" mb={2}>Revedy Recommended</Text>
                        <Text fontSize="sm"><strong>ICD-10:</strong> M51.04 (Disc disorder with myelopathy)</Text>
                        <Text fontSize="sm"><strong>ICD-10:</strong> R20.2 (Paresthesia of skin)</Text>
                        <Text fontSize="sm" mt={2} color="green.600"><em>Result: Establishes medical necessity, bypassing manual review.</em></Text>
                      </Box>
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>

                {/* Case Study 2 */}
                <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="md">
                  <AccordionButton _expanded={{ bg: 'blue.50' }}>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Badge colorScheme="green">Net New Codes Found</Badge>
                        <Text fontWeight="bold">Case #<Text as="span" filter="blur(3px)">3772289</Text>: The Uncaptured Revenue Fix</Text>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4} bg="white">
                    <Text fontSize="sm" mb={4}>
                      <strong>Story:</strong> The biller missed the time-based add-on codes. Revedy's automated system extracted the exact start/stop times from the technical report and added 4 units of the appropriate add-on code.
                    </Text>
                    <SimpleGrid columns={2} spacing={4}>
                      <Box p={3} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.100">
                        <Text fontWeight="bold" color="red.700" mb={2}>Original Claim</Text>
                        <Text fontSize="sm"><strong>Primary Code:</strong> Billed Correctly</Text>
                        <Text fontSize="sm"><strong>Add-on Code:</strong> Missing</Text>
                        <Text fontSize="sm" mt={2} color="red.600"><em>Result: <Text as="span" filter="blur(3px)">$450</Text> left on the table.</em></Text>
                      </Box>
                      <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.100">
                        <Text fontWeight="bold" color="green.700" mb={2}>Revedy Recommended</Text>
                        <Text fontSize="sm"><strong>Primary Code:</strong> Billed Correctly</Text>
                        <Text fontSize="sm"><strong>Add-on Code:</strong> Added with 4 Units</Text>
                        <Text fontSize="sm" mt={2} color="green.600"><em>Justification: "Technical Report documents 20 distinct responses. Coding rules state to count distinct runs, capping at 5 units."</em></Text>
                      </Box>
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>

              </Accordion>
            </CardBody>
          </Card>

        </VStack>
      </Container>
    </Box>
  );
}
